import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MailCheck, CheckCircle2, XCircle } from 'lucide-react';
import MemberLayout from '../../components/MemberLayout';
import { PageLoader, Spinner } from '../../components/ui';
import api, { errMsg } from '../../services/api';
import { useMember } from '../../context/AuthContext';

function Verified() {
  return (
    <div className="text-center">
      <CheckCircle2 size={52} className="mx-auto text-green-600" />
      <h1 className="mt-3 font-display text-3xl font-bold text-navy-900">Email verified</h1>
      <p className="mt-2 text-sm text-gray-600">You're now an active member. You'll receive batch news and can open the Group documents library.</p>
      <div className="mt-6 flex justify-center gap-3">
        <Link to="/documents" className="btn-gold">Group documents</Link>
        <Link to="/profile" className="btn-outline">My profile</Link>
      </div>
    </div>
  );
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { member, loading, signIn } = useMember();
  const [state, setState] = useState(token ? 'checking' : 'code');
  const [linkError, setLinkError] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  // Verification by link
  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    api.post('/public/verify-email', { token })
      .then(({ data }) => { signIn(data.token, data.member); setState('done'); })
      .catch(err => { setLinkError(errMsg(err, 'This link is invalid')); setState('error'); });
  }, [token, signIn]);

  const submitCode = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/member/me/verify-code', { code });
      signIn(data.token, data.member);
      setState('done');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post('/member/me/resend-verification');
      toast.success(`A new email was sent to ${data.email}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  let content;
  if (state === 'checking' || loading) content = <PageLoader />;
  else if (state === 'done' || (member && member.status === 'ACTIVE' && !member.pendingEmail && !token)) content = <Verified />;
  else if (state === 'error' && !member) {
    content = (
      <div className="text-center">
        <XCircle size={48} className="mx-auto text-red-500" />
        <h1 className="mt-3 text-xl font-semibold text-navy-900">Link not valid</h1>
        <p className="mt-2 text-sm text-gray-600">{linkError}</p>
        <Link to="/login" className="btn-primary mt-6">Log in to get a new code</Link>
      </div>
    );
  } else if (!member) {
    content = (
      <div className="text-center">
        <p className="text-sm text-gray-600">Please log in to enter your verification code.</p>
        <Link to="/login" className="btn-primary mt-4">Log in</Link>
      </div>
    );
  } else {
    const target = member.pendingEmail || member.email;
    content = (
      <div>
        {state === 'error' && <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{linkError}</p>}
        <div className="text-center">
          <MailCheck size={48} className="mx-auto text-navy-700" />
          <h1 className="mt-3 font-display text-3xl font-bold text-navy-900">Verify your email</h1>
          <p className="mt-2 text-sm text-gray-600">We sent a link and a 6-digit code to <strong>{target}</strong>. Click the link, or enter the code below.</p>
        </div>
        <form onSubmit={submitCode} className="mt-6 space-y-4">
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••"
            className="input py-3 text-center font-mono text-2xl tracking-[0.5em]" />
          <button disabled={busy || code.length !== 6} className="btn-primary w-full py-2.5">{busy && <Spinner size={16} />}Verify</button>
        </form>
        <p className="mt-5 text-center text-sm text-gray-600">
          Didn't get it? Check your spam folder, or <button onClick={resend} className="font-medium text-navy-700 underline">send a new code</button>.
        </p>
      </div>
    );
  }

  return <MemberLayout narrow><div className="card p-7">{content}</div></MemberLayout>;
}
